import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, RotateCcw, FileText, Paperclip, Loader2 } from "lucide-react";

export default function RecycleBin({ engId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const load = () => {
    setData(null);
    api.get(`/m1/engagements/${engId}/recycle-bin`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  };
  const onOpenChange = (o) => { setOpen(o); if (o) load(); };

  const restoreRequest = async (r) => {
    try {
      await api.post(`/m1/engagements/${engId}/bulk-action`, { request_ids: [r.id], action: "restore" });
      toast.success("Request restored"); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const restoreFile = async (a) => {
    try {
      await api.post(`/m1/attachments/${a.id}/restore`);
      toast.success("File restored"); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const empty = data && data.requests.length === 0 && data.attachments.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="recycle-bin-btn">
          <Trash2 className="mr-2 h-4 w-4" /> Recycle bin
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Recycle bin</DialogTitle>
          <DialogDescription>
            Deleted requests and files stay here for {data?.retention_days ?? 30} days, then delete permanently.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : empty ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="recycle-empty">The recycle bin is empty.</p>
        ) : (
          <div className="space-y-5">
            {data.requests.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Deleted requests</div>
                <div className="space-y-2">
                  {data.requests.map((r) => (
                    <div key={r.id} data-testid={`recycle-req-${r.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{r.name}</div>
                          <div className="text-[11px] text-slate-400">Auto-deletes in {r.days_left} day{r.days_left === 1 ? "" : "s"}</div>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 text-indigo-600 hover:text-indigo-700" data-testid={`recycle-restore-req-${r.id}`} onClick={() => restoreRequest(r)}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.attachments.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Deleted files</div>
                <div className="space-y-2">
                  {data.attachments.map((a) => (
                    <div key={a.id} data-testid={`recycle-file-${a.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{a.filename}</div>
                          <div className="text-[11px] text-slate-400">Removed by {a.deleted_by_name} · auto-deletes in {a.days_left} day{a.days_left === 1 ? "" : "s"}</div>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 text-indigo-600 hover:text-indigo-700" data-testid={`recycle-restore-file-${a.id}`} onClick={() => restoreFile(a)}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
